import { useMemo, useState } from 'react';
import { Button, ChatMessageBubble, ExpandableText, SparkIcon, Text, ToolCallCard, TooltipTrigger } from '@spark';
import { DatasetOrigin } from '@/shared/domain/taskTypes';
import { groupTrajectory, trajectoryMessages } from './trajectoryView';
import './AgenticExampleCell.css';

/** Response origins that mean the trajectory was produced by the teacher model. */
const AI_TRAJECTORY_ORIGINS = new Set([
    DatasetOrigin.specific_ai_labeled,
    DatasetOrigin.specific_ai_fully_generated,
]);

export const AI_TRAJECTORY_TOOLTIP = 'Trajectory generated by SpecificAI';
export const ORIGIN_TRAJECTORY_TITLE = 'Original trajectory';
export const ORIGIN_TRAJECTORY_NOTE = 'Kept from your file as a reference. Not used for training.';

/** True when the record's trajectory was generated by the platform (AI origin). */
export function isAiGeneratedTrajectory(record) {
    return AI_TRAJECTORY_ORIGINS.has(record?.response?.response_origin);
}

/**
 * Grouped turns of `record.origin_trajectory` — the file's original
 * trajectory, snapshotted when the platform generated a new one. `[]` when
 * the record has none.
 */
export function originTrajectoryItems(record) {
    const origin = record?.origin_trajectory;
    if (!Array.isArray(origin) || origin.length === 0) return [];
    return groupTrajectory(trajectoryMessages({ trajectory: origin }));
}

/** Line clamps before Spark `ExpandableText` offers "Show more" (Figma rows show 2–3 lines). */
const META_MAX_LINES = 3;
const BUBBLE_MAX_LINES = 4;

/**
 * Controls inside the example area (the chevron, "Show more", the
 * classification / NER example editor) keep their own click behaviour; a
 * single click anywhere else on the cell — the header, the gap below it, or
 * the collapsed preview — toggles the row.
 */
const INTERACTIVE_SELECTOR =
    'button, a, input, textarea, select, [contenteditable="true"], [role="button"], [role="textbox"]';

/** True when a click landed on an interactive control inside `container`. */
export function isInteractiveTarget(target, container) {
    const hit = target?.closest?.(INTERACTIVE_SELECTOR);
    return Boolean(hit && container?.contains(hit));
}

/**
 * True when a click landed inside the expanded conversation. Bubble text
 * there is meant to be selectable and the tool-call cards have their own
 * controls, so a click on it must not collapse the row.
 */
function isExpandedConversationTarget(target, container) {
    const hit = target?.closest?.('.agentic-example-cell__conversation');
    return Boolean(
        hit &&
            container?.contains(hit) &&
            !hit.classList.contains('agentic-example-cell__conversation--collapsed'),
    );
}

/**
 * Italic `Example: "…"` / `Response: "…"` line of the agentic EXAMPLE cell
 * (Figma 3966:24151 / 3966:24152). Clamped by Spark `ExpandableText`.
 */
function MetaLine({ label, text, testId }) {
    return (
        <div className="agentic-example-cell__meta-line" data-testid={testId}>
            <ExpandableText maxLines={META_MAX_LINES} color="primary">
                <span className="agentic-example-cell__meta-label">{label}:</span>{' '}
                <span className="agentic-example-cell__meta-text">{text}</span>
            </ExpandableText>
        </div>
    );
}

function TrajectoryItem({ item }) {
    if (item.kind === 'tool_call') {
        // The card's code panes scroll and are height-resizable (Figma
        // 3968:28510), so the raw text goes in without a line clamp.
        return (
            <ToolCallCard
                name={item.name}
                summary={item.summary}
                argumentsText={item.arguments}
                responseText={item.response}
            />
        );
    }
    return (
        <ChatMessageBubble role={item.role}>
            <ExpandableText text={item.content} maxLines={BUBBLE_MAX_LINES} color="primary" />
        </ChatMessageBubble>
    );
}

/**
 * EXAMPLE cell of an agentic task row (Figma 3966:24148 expanded, 3967:25988
 * collapsed).
 *
 * - Generative tasks: italic `Example:` and `Response:` lines from the record.
 * - Classification / NER pass their existing example editor as `header` so
 *   the Labels / Entities columns keep working; only the trajectory is added.
 * - When the record carries a trajectory, a chevron — or a single click on
 *   the example text area or the collapsed preview below it — toggles
 *   between the collapsed preview (first bubble clipped to 24px under a
 *   white fade) and the full conversation:
 *   user bubbles left, assistant bubbles right, and a `ToolCallCard` for each
 *   assistant tool call paired with its tool response.
 */
export function AgenticExampleCell({
    record,
    exampleText,
    responseText,
    showResponse = false,
    header = null,
    defaultExpanded = false,
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [originExpanded, setOriginExpanded] = useState(false);
    const items = useMemo(() => groupTrajectory(trajectoryMessages(record)), [record]);
    const originItems = useMemo(() => originTrajectoryItems(record), [record]);
    const hasTrajectory = items.length > 0;
    const hasOriginTrajectory = originItems.length > 0;
    const aiGenerated = hasTrajectory && isAiGeneratedTrajectory(record);

    const handleCellClick = (event) => {
        if (
            !hasTrajectory ||
            isInteractiveTarget(event.target, event.currentTarget) ||
            isExpandedConversationTarget(event.target, event.currentTarget)
        ) {
            return;
        }
        // Releasing a drag that selected some of the example text also fires
        // a click; leave the selection alone instead of toggling the row.
        if (window.getSelection?.()?.toString()) {
            return;
        }
        setExpanded((value) => !value);
    };

    const meta = header !== null ? (
        <div className="agentic-example-cell__header-slot">{header}</div>
    ) : (
        <div className="agentic-example-cell__meta">
            <MetaLine label="Example" text={exampleText ?? ''} testId="agentic-example-meta" />
            {showResponse ? (
                <MetaLine label="Response" text={responseText ?? ''} testId="agentic-response-meta" />
            ) : null}
        </div>
    );

    return (
        <div
            className="agentic-example-cell"
            data-testid="agentic-example-cell"
            data-expanded={hasTrajectory ? String(expanded) : undefined}
            // On the wrapper, not the header, so the gap under the header and
            // the collapsed preview (which is `pointer-events: none` and lets
            // the click fall through to this element) toggle as well.
            onClick={hasTrajectory ? handleCellClick : undefined}
        >
            <div className="agentic-example-cell__header" data-testid="agentic-example-header">
                {meta}
                {aiGenerated ? (
                    <TooltipTrigger text={AI_TRAJECTORY_TOOLTIP} placement="top">
                        <span
                            className="agentic-example-cell__ai-badge"
                            data-testid="agentic-trajectory-ai-badge"
                            aria-label={AI_TRAJECTORY_TOOLTIP}
                        >
                            <SparkIcon slug="sparkles-2" size="small" />
                        </span>
                    </TooltipTrigger>
                ) : null}
                {hasTrajectory ? (
                    <Button
                        variant="ghost"
                        size="tiny"
                        className="agentic-example-cell__toggle"
                        aria-expanded={expanded}
                        aria-label={expanded ? 'Hide trajectory' : 'Show trajectory'}
                        tooltip={expanded ? 'Hide trajectory' : 'Show trajectory'}
                        icon={<SparkIcon slug={expanded ? 'chevron-up-large' : 'chevron-down-large'} size="medium" />}
                        onClick={() => setExpanded((value) => !value)}
                    />
                ) : null}
            </div>
            {hasTrajectory && expanded ? (
                <div className="agentic-example-cell__conversation" data-testid="agentic-trajectory">
                    {items.map((item) => (
                        <TrajectoryItem key={item.key} item={item} />
                    ))}
                </div>
            ) : null}
            {hasTrajectory && expanded && hasOriginTrajectory ? (
                // Read-only reference to the file's original trajectory (the
                // one the platform replaced). Shares the conversation class so
                // clicks inside it never collapse the row.
                <div
                    className="agentic-example-cell__conversation agentic-example-cell__origin"
                    data-testid="agentic-origin-trajectory"
                >
                    <div className="agentic-example-cell__origin-header">
                        <div className="agentic-example-cell__origin-title">
                            <Text variant="detail-semibold" color="secondary">{ORIGIN_TRAJECTORY_TITLE}</Text>
                            <Text variant="detail-regular" color="secondary">{ORIGIN_TRAJECTORY_NOTE}</Text>
                        </div>
                        <Button
                            variant="ghost"
                            size="tiny"
                            className="agentic-example-cell__toggle"
                            aria-expanded={originExpanded}
                            aria-label={originExpanded ? 'Hide original trajectory' : 'Show original trajectory'}
                            tooltip={originExpanded ? 'Hide original trajectory' : 'Show original trajectory'}
                            icon={<SparkIcon slug={originExpanded ? 'chevron-up-large' : 'chevron-down-large'} size="medium" />}
                            onClick={() => setOriginExpanded((value) => !value)}
                        />
                    </div>
                    {originExpanded ? (
                        <div className="agentic-example-cell__conversation" data-testid="agentic-origin-trajectory-turns">
                            {originItems.map((item) => (
                                <TrajectoryItem key={item.key} item={item} />
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {hasTrajectory && !expanded ? (
                // Figma 3967:25994: the first turn clipped to 24px under a
                // white fade. Decorative: it takes no pointer events itself, so
                // a click on it reaches the wrapper and expands the row.
                <div
                    className="agentic-example-cell__conversation agentic-example-cell__conversation--collapsed"
                    data-testid="agentic-trajectory-preview"
                    aria-hidden="true"
                >
                    <TrajectoryPreview item={items[0]} />
                </div>
            ) : null}
        </div>
    );
}

function TrajectoryPreview({ item }) {
    if (item.kind === 'tool_call') {
        return (
            <ChatMessageBubble role="tool">
                {item.summary ? `${item.name} → ${item.summary}` : item.name}
            </ChatMessageBubble>
        );
    }
    return <ChatMessageBubble role={item.role}>{item.content}</ChatMessageBubble>;
}

export default AgenticExampleCell;
